import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { MonthlyDREData } from "@/hooks/useDREMonthlyData";
import { TrendingUp } from "lucide-react";

interface NetProfitEvolutionChartProps {
  data: MonthlyDREData[];
}

const chartConfig = {
  netRevenue: {
    label: "Receita Líquida",
    color: "hsl(var(--chart-1))",
  },
  grossProfit: {
    label: "Lucro Bruto",
    color: "hsl(var(--chart-2))",
  },
  operatingResult: {
    label: "Resultado Operacional",
    color: "hsl(var(--chart-3))",
  },
  netProfit: {
    label: "Lucro Líquido",
    color: "hsl(var(--chart-4))",
  },
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

function formatShortCurrency(value: number): string {
  const absValue = Math.abs(value / 100);
  if (absValue >= 1000000) {
    return `R$ ${(value / 100 / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `R$ ${(value / 100 / 1000).toFixed(1)}K`;
  }
  return formatCurrency(value);
}

export function NetProfitEvolutionChart({ data }: NetProfitEvolutionChartProps) {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4" />
            Evolução Mensal
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center text-muted-foreground">
          Sem dados para exibir
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4" />
          Evolução Mensal do Resultado
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <LineChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="monthLabel" 
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis 
              tickFormatter={formatShortCurrency}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={60}
            />
            <ChartTooltip 
              content={
                <ChartTooltipContent 
                  formatter={(value) => formatCurrency(Number(value))}
                />
              } 
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="netRevenue"
              name="Receita Líquida"
              stroke="hsl(var(--chart-1))"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="grossProfit"
              name="Lucro Bruto"
              stroke="hsl(var(--chart-2))"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="netProfit"
              name="Lucro Líquido"
              stroke="hsl(var(--chart-4))"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
